import * as Sentry from "@sentry/react";
import { isAutomatedBrowser } from "@/services/sentry-capture-policy";
import { prepareApiHealthSpan } from "@/services/sentry-api-health";

function getDsn(): string {
  return import.meta.env.VITE_SENTRY_DSN ?? "";
}

function getEnvironment(): string {
  return import.meta.env.VITE_SENTRY_ENVIRONMENT || "local";
}

function getRelease(): string | undefined {
  return import.meta.env.VITE_SENTRY_RELEASE || undefined;
}

function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || "";
}

export function sentryIsEnabled(): boolean {
  return getDsn().length > 0;
}

export function initSentry(): void {
  const dsn = getDsn();
  if (dsn.length === 0) return;

  // The API runs on a different origin than the app in deployed envs
  // (VITE_API_BASE_URL), and Sentry only propagates the `sentry-trace` /
  // `baggage` headers to same-origin + localhost by default. Without the API
  // origin listed here the browser span and the Django span never join into a
  // single distributed trace. The backend allow-lists those two request
  // headers in CORS_ALLOW_HEADERS so the cross-origin preflight passes.
  const apiBaseUrl = getApiBaseUrl();
  const tracePropagationTargets: (string | RegExp)[] = ["localhost", /^\/(?:api|auth)\//];
  if (apiBaseUrl) tracePropagationTargets.push(apiBaseUrl);

  Sentry.init({
    dsn,
    environment: getEnvironment(),
    release: getRelease(),
    sendDefaultPii: false,
    tracesSampleRate: 0.1,
    tracePropagationTargets,
    // Structured logs (Sentry Logs). consoleLoggingIntegration forwards
    // console.warn / console.error as logs, correlated to the active trace and
    // any captured error. console.log is intentionally excluded to keep noise
    // (and log quota) down.
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay, captured only when an error fires (no always-on session
      // sampling) to stay within the included replay quota. Text and media are
      // masked by default so no PII is recorded.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Drop events from automated (E2E / bot) browsers so the staging Playwright
    // suite — role personas deliberately hitting forbidden pages, plus the
    // main-suite `/` and `/workspace-invitations` traffic — doesn't flood real
    // signal (#2015). We send no PII by default (no email / user id), so
    // `navigator.webdriver` is the one runtime signal actually present for E2E
    // traffic; Playwright's Chromium sets it and no real user's browser does.
    beforeSend: (event) => (isAutomatedBrowser() ? null : event),
    beforeSendSpan: (span) => prepareApiHealthSpan(span, apiBaseUrl, isAutomatedBrowser()),
    // Drop known third-party / browser noise so it doesn't bury real issues or
    // burn the error quota.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
    denyUrls: [
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
  });
}
